import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Button,
} from "@mui/material";
import {
  Edit,
  DeleteOutline,
  SecurityOutlined,
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
} from "@mui/icons-material";
import SelectField from "../../atoms/SelectField/SelectField";
import ConfirmDialog from "../../atoms/ConfirmDialog/ConfirmDialog";
import { deleteRole, findAllRoles } from "../../../API/apiRoles";
import { SystemRole } from "../../../types/SystemRole";
import { useAuth } from "../../../resources/AuthProvider";
import styles from "./GestaoPerfis.module.css";
import styles_table from "../../../styles/Table.module.css";
import styles_button from "../../../styles/Button.module.css";
import styles_general from "../../../styles/General.module.css";

const GestaoPerfis = () => {
  const navigate = useNavigate();
  const { userPageList } = useAuth();
  const canAccessForm = userPageList.includes("novo-perfil");
  const canManagePermissions = userPageList.includes("permissoes");

  const [roles, setRoles] = useState<SystemRole[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingConfirmDialog, setLoadingConfirmDialog] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [selectedRoleName, setSelectedRoleName] = useState<string>("");

  useEffect(() => {
    getRoles();
  }, [currentPage, perPage]);

  useEffect(() => {
    if (!msgSuccess) return;
    const timer = setTimeout(() => setMsgSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSuccess]);

  useEffect(() => {
    if (!msgError) return;
    const timer = setTimeout(() => setMsgError(null), 5000);
    return () => clearTimeout(timer);
  }, [msgError]);

  const getRoles = async () => {
    setLoading(true);
    try {
      const response = await findAllRoles(currentPage, perPage);
      setRoles(response.roles || []);
      setTotalPages(response.pagination?.last_page || 1);
    } catch (error) {
      setMsgError("Não foi possível listar os perfis.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDelete = (role: SystemRole) => {
    setSelectedRoleId(role.id);
    setSelectedRoleName(role.nome);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    if (!selectedRoleId) return;

    try {
      setLoadingConfirmDialog(true);
      await deleteRole(selectedRoleId);
      setMsgSuccess("Perfil excluído com sucesso.");
      setOpenDelete(false);
      setSelectedRoleId(null);
      setSelectedRoleName("");
      await getRoles();
    } catch (error) {
      setMsgError("Não foi possível excluir o perfil.");
    } finally {
      setLoadingConfirmDialog(false);
    }
  };

  return (
    <Card className={styles.card}>
      <CardContent>
        <div className={styles.title}>Gestão de Perfis</div>

        {canAccessForm && (
          <div className={styles.actionsHeader}>
            <Button
              variant="contained"
              className={styles_button.buttonSquarePrimary}
              onClick={() => navigate("/novo-perfil")}
            >
              Novo Perfil
            </Button>
          </div>
        )}

        {msgSuccess && (
          <div className={styles_table.message}>
            <Alert severity="success">{msgSuccess}</Alert>
          </div>
        )}

        {msgError && (
          <div className={styles_table.message}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}

        <TableContainer className={styles_table.tableContainer}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Table size="small">
              <TableHead className={styles_table.tableHead}>
                <TableRow>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Nome</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Descrição</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {roles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" className={styles_table.textXs}>
                      Nenhum registro encontrado
                    </TableCell>
                  </TableRow>
                )}

                {roles.map((role, index) => (
                  <TableRow
                    key={role.id}
                    className={`${styles_table.tableRow} ${index % 2 === 0 ? styles_table.tableRowEven : styles_table.tableRowOdd}`}
                  >
                    <TableCell padding="none" className={styles_table.textXs}>{role.nome}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>{role.descricao}</TableCell>
                    <TableCell padding="none">
                      <div className={styles_table.blockActions}>
                        {canAccessForm && (
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Editar Perfil"
                            onClick={() => navigate(`/novo-perfil?id=${role.id}`)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                        {canManagePermissions && (
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Permissões do Perfil"
                            onClick={() => navigate(`/permissoes?role_id=${role.id}`)}
                          >
                            <SecurityOutlined fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Excluir Perfil"
                          onClick={() => handleOpenDelete(role)}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {roles.length > 0 && (
          <div className={`${styles_table.containerPagination} ${styles_general.mt4}`}>
            <div className={styles_table.qtdItems}>
              <div className={styles_table.labelSelect}>Resultados por página</div>
              <SelectField
                label=""
                value={perPage.toString()}
                onChange={(value) => setPerPage(Number(value))}
                options={[{ nome: "10" }, { nome: "20" }, { nome: "30" }]}
                disableClearable
                disableTyping
              />
            </div>

            <div className={styles_table.pagination}>
              <IconButton
                className={currentPage === 1 ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
                aria-label="Primeira página"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                <KeyboardDoubleArrowLeftOutlined />
              </IconButton>

              <IconButton
                className={currentPage === 1 ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
                aria-label="Página anterior"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <KeyboardArrowLeftOutlined />
              </IconButton>

              <span className={styles_table.pages}>Página {currentPage} de {totalPages}</span>

              <IconButton
                className={currentPage === totalPages ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
                aria-label="Próxima página"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                <KeyboardArrowRightOutlined />
              </IconButton>

              <IconButton
                className={currentPage === totalPages ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
                aria-label="Última página"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                <KeyboardDoubleArrowRightOutlined />
              </IconButton>
            </div>

            <div className={styles_table.setNumberPage}>
              <div className={styles_table.labelSelect}>Ir para a página</div>
              <SelectField
                label=""
                value={currentPage.toString()}
                onChange={(value) => setCurrentPage(Number(value))}
                options={Array.from({ length: totalPages }, (_, index) => ({
                  nome: String(index + 1),
                }))}
                disableTyping
              />
            </div>
          </div>
        )}
      </CardContent>

      <ConfirmDialog
        open={openDelete}
        loading={loadingConfirmDialog}
        onClose={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        title="Excluir Perfil"
        message={`Deseja realmente excluir o perfil "${selectedRoleName}"?`}
        titleLoadButton="Excluindo..."
        titleButtonConfirm="Excluir"
      />
    </Card>
  );
};

export default GestaoPerfis;
